import type { FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import * as FormDialog from "../../../../shared/FormDialog";
import type { OrganisationHierarchy } from "../../../../api/organisations.types";
import { useCreateTenant } from "../../../../api/tenants";
import { isTenantIdAlreadyAddedErrorResponse } from "../../../../api/tenants.types";

type FormValues = { organizationName: string; organizationNumber: string };
type AddChildDialogProps = { tenant: OrganisationHierarchy; onClose: () => void };
export const AddChildDialog: FC<AddChildDialogProps> = ({ tenant, onClose }) => {
  const { t } = useTranslation();
  const { mutate, isError, isPending, error } = useCreateTenant();
  const tenantAlreadyAdded = isTenantIdAlreadyAddedErrorResponse(error);
  const { handleSubmit, reset, control, setFocus } = useForm<FormValues>({
    defaultValues: { organizationName: "", organizationNumber: tenant.organizationNumber },
  });

  FormDialog.useAutoFocusField(setFocus, "organizationName");

  const createTenant = ({ organizationName, organizationNumber }: FormValues) => {
    mutate({ organizationName, organizationNumber, parentId: tenant.id }, { onSuccess: handleClose });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <FormDialog.Dialog isOpen>
      <FormDialog.Title>{t("OrganisationStructure.AddChildForm.Title")}</FormDialog.Title>

      <FormDialog.Form onSubmit={handleSubmit(createTenant)}>
        <FormDialog.Content>
          <FormDialog.FormError
            isError={isError}
            errorMessage={tenantAlreadyAdded ? t("OrganisationStructure.AddChildForm.DuplicateNameError") : undefined}
          />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="organizationName"
              rules={{ required: t("OrganisationStructure.AddChildForm.NameValidation") }}
              label={t("OrganisationStructure.AddChildForm.NameLabel")}
            />

            <FormDialog.TextField
              control={control}
              name="organizationNumber"
              rules={{ required: t("OrganisationStructure.AddChildForm.OrgNumberValidation") }}
              label={t("OrganisationStructure.AddChildForm.OrgNumberLabel")}
              placeholder={t("OrganisationStructure.AddChildForm.OrgNumberPlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={handleClose} />
          <FormDialog.SubmitButton isSubmitting={isPending}>
            {t("OrganisationStructure.AddChildForm.Submit")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
